import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Phone } from "lucide-react";
import { Reveal, Tag, Heading } from "../Reveal";
import { CTA } from "../CTA";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Residential remodel", "Light commercial", "New construction / addition", "Custom carpentry", "Other"];
const AREAS = ["Delaware", "Pennsylvania", "New Jersey", "Baltimore"];

const inputCls = "rounded-none border-0 border-b border-ink/25 bg-transparent h-12 px-0 text-base text-ink shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-ink placeholder:text-ink/30 transition-colors";
const labelCls = "font-mono text-[11px] uppercase tracking-[0.16em] text-ink/55";

const Chip = ({ active, onClick, testId, children }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={`font-sans font-medium text-sm px-4 py-2.5 border transition-colors duration-300 ${active ? "bg-ink text-bone border-ink" : "border-ink/20 text-ink/70 hover:border-ink hover:text-ink"}`}>
    {children}
  </button>
);

export const EstimateForm = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", project_type: "", location: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pick = (k, v) => setForm((f) => ({ ...f, [k]: f[k] === v ? "" : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please add your name, email and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setDone(true);
      toast.success("Request received — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="estimate" className="py-24 lg:py-40 bg-sand border-t border-ink/10" data-testid="estimate-section">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Tag>Ready to start your project?</Tag>
            <Heading className="mt-6">Get a free, no-obligation estimate.</Heading>
            <p className="mt-8 text-base sm:text-lg text-ink/70 leading-relaxed max-w-md">
              Tell us about your residential or commercial project. We'll work with you to bring your vision to life — starting with an honest number and a clear plan.
            </p>
            <a href="tel:3022301988" data-testid="estimate-call-link" className="mt-10 inline-flex items-center gap-4 font-display font-medium text-2xl sm:text-3xl tracking-[-0.015em] text-ink hover:text-teal transition-colors">
              <span className="h-12 w-12 bg-ink text-bone flex items-center justify-center"><Phone className="h-5 w-5" /></span>
              (302) 230-1988
            </a>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          {done ? (
            <div className="min-h-[380px] flex flex-col justify-center border border-ink/15 bg-bone p-10 sm:p-14" data-testid="estimate-form-success">
              <span className="h-12 w-12 bg-teal text-bone flex items-center justify-center"><Check className="h-6 w-6" strokeWidth={3} /></span>
              <h3 className="mt-8 font-display font-semibold text-4xl sm:text-5xl tracking-[-0.02em] text-ink">Thank you — we've got it.</h3>
              <p className="mt-5 text-base sm:text-lg text-ink/70 max-w-md leading-relaxed">We'll review your project details and reach out shortly to talk through next steps.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="estimate-form">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <Label htmlFor="ef-name" className={labelCls}>Full name *</Label>
                  <Input id="ef-name" data-testid="estimate-name-input" className={`mt-1 ${inputCls}`} placeholder="Jane Doe" value={form.full_name} onChange={set("full_name")} required />
                </div>
                <div>
                  <Label htmlFor="ef-phone" className={labelCls}>Phone number *</Label>
                  <Input id="ef-phone" type="tel" data-testid="estimate-phone-input" className={`mt-1 ${inputCls}`} placeholder="(555) 234-5678" value={form.phone} onChange={set("phone")} required />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ef-email" className={labelCls}>Email address *</Label>
                  <Input id="ef-email" type="email" data-testid="estimate-email-input" className={`mt-1 ${inputCls}`} placeholder="jane@example.com" value={form.email} onChange={set("email")} required />
                </div>
                <div className="sm:col-span-2" data-testid="estimate-project-type">
                  <Label className={labelCls}>Project type</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {TYPES.map((t) => <Chip key={t} active={form.project_type === t} onClick={() => pick("project_type", t)} testId={`project-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</Chip>)}
                  </div>
                </div>
                <div className="sm:col-span-2" data-testid="estimate-location">
                  <Label className={labelCls}>Project location</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {AREAS.map((a) => <Chip key={a} active={form.location === a} onClick={() => pick("location", a)} testId={`location-${a.toLowerCase().replace(/\s+/g, "-")}`}>{a}</Chip>)}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ef-msg" className={labelCls}>Tell us about the project</Label>
                  <Textarea id="ef-msg" data-testid="estimate-message-input" className="mt-1 rounded-none border-0 border-b border-ink/25 bg-transparent min-h-[110px] px-0 py-3 text-base text-ink shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-ink placeholder:text-ink/30" placeholder="Scope, timeline, anything we should know..." value={form.message} onChange={set("message")} />
                </div>
              </div>
              <CTA type="submit" disabled={sending} testId="estimate-submit-button" className="mt-12 w-full !py-6 text-base sm:text-lg">
                {sending ? <span className="inline-flex items-center gap-3"><Loader2 className="h-4 w-4 animate-spin" /> Sending...</span> : "Request my free estimate"}
              </CTA>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
